import { useEffect, useRef, useState } from "react";
import { Form, Input, Skeleton } from "../../components/ui";
import {
  useAddAdvertisement,
  useGetAdvertisementById,
  useUpdateAdvertisement,
} from "../../query/advertisementQuery";
import FormPhotoDrop from "../../components/FormPhotoDrop";
import swal from "../../utils/swal";
import { useNavigate, useParams } from "react-router-dom";
import { UPLOADS_URL } from "../../config/constants";
import "../teachers/addTeacher.css";

function AddAdvertisement() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addAd, isPending: isAdding } = useAddAdvertisement();
  const { mutate: updateAd, isPending: isUpdating } = useUpdateAdvertisement();
  const { data: advertisement, isLoading, isError, error } = useGetAdvertisementById(
    isEdit ? id : undefined
  );
  const intentRef = useRef("ACTIVE");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !advertisement) return;
    form.setFieldsValue({
      title: advertisement.title || "",
      link: advertisement.link || "",
      order: advertisement.order ?? 0,
    });
    if (advertisement.image) {
      setImagePreview(`${UPLOADS_URL}/${advertisement.image}`);
    }
    intentRef.current =
      advertisement.status === "INACTIVE" ? "INACTIVE" : "ACTIVE";
  }, [isEdit, advertisement, form]);

  const buildFormData = (values) => {
    const formObject = new FormData();
    if (imageFile) formObject.append("image", imageFile);
    formObject.append("title", values.title || "");
    formObject.append("link", values.link || "");
    formObject.append("order", String(values.order ?? 0));
    formObject.append(
      "status",
      intentRef.current === "INACTIVE" ? "INACTIVE" : "ACTIVE"
    );
    return formObject;
  };

  const onFinish = (values) => {
    if (!isEdit && !imageFile) {
      swal("Oops!", "Please upload an advertisement image", "error");
      return;
    }

    const formObject = buildFormData(values);

    if (isEdit) {
      updateAd(
        { id, data: formObject },
        {
          onSuccess: () => {
            swal("Success", "Advertisement updated successfully", "success");
            navigate("/advertisements");
          },
          onError: (err) => {
            swal("Error", err?.message || "Failed to update advertisement", "error");
          },
        }
      );
      return;
    }

    addAd(
      { data: formObject },
      {
        onSuccess: () => {
          const drafted = intentRef.current === "INACTIVE";
          swal(
            "Success",
            drafted
              ? "Advertisement saved as inactive"
              : "Advertisement added successfully",
            "success"
          );
          navigate("/advertisements");
        },
        onError: (err) => {
          swal("Error", err?.message || "Failed to add advertisement", "error");
        },
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  if (isEdit && (isError || !advertisement)) {
    return (
      <div className="tf-page">
        <section className="tf-card">
          <h2 className="tf-card-head">Advertisement</h2>
          <div className="tf-card-body">
            <p className="tf-hint">
              {error?.message || "Advertisement not found."}
            </p>
            <div className="tf-actions" style={{ paddingTop: 0 }}>
              <button
                type="button"
                className="tf-btn is-ghost"
                onClick={() => navigate("/advertisements")}
              >
                Back to list
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <section className="tf-card">
          <h2 className="tf-card-head">Advertisement</h2>
          <div className="tf-card-body">
            <FormPhotoDrop
              preview={imagePreview}
              required={!isEdit}
              label="Banner image"
              alt="Advertisement preview"
              variant="rect"
              hint="PNG, JPG or WEBP — wide banner works best"
              onFile={(file) => {
                setImageFile(file);
                setImagePreview((prev) => {
                  if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(file);
                });
              }}
            />

            <div className="tf-field">
              <p className="tf-label">
                Title <span>*</span>
              </p>
              <Form.Item
                name="title"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input placeholder="Open Day & Campus Tour" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Link (optional)</p>
              <Form.Item name="link" className="tf-item mb-0">
                <Input placeholder="https://..." />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Display order</p>
              <Form.Item name="order" className="tf-item mb-0" initialValue={0}>
                <Input type="number" placeholder="0" />
              </Form.Item>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button
            type="button"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => navigate("/advertisements")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "INACTIVE";
            }}
          >
            Save inactive
          </button>
          <button
            type="submit"
            className="tf-btn is-primary"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "ACTIVE";
            }}
          >
            {isMutating
              ? "Saving..."
              : isEdit
              ? "Update"
              : "Publish"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddAdvertisement;
